"use client";

import SimpleMonacoEditor from "@/components/CodeEditors/SimpleMonacoEditor";
import { useSupabase } from "@/components/contexts/DatabaseSyncWrapper";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useAiActionStore } from "@/store/zustand/aiActionStore";
import { useIsChatLoading } from "@/store/zustand/aiContextStore";
import {
  ManualAssistantMessage,
  useAiContextStore,
} from "@/store/zustand/aiContextStore";
import useLayoutStore, {
  LayoutMode,
  LeftTab,
} from "@/store/zustand/layoutStore";
import usePlanInfoStore from "@/store/zustand/planInfoStore";
import { useProjectCreateStore } from "@/store/zustand/projectCreateStore";
import {
  useFeaturePrdForCanvas,
  useSupabaseStore,
} from "@/store/zustand/supabaseStore";
import { generateJsonStream } from "@/utils/ai/utils";
import { track } from "@/utils/events/tracker";
import {
  fixJson,
  getUnexpiredToken,
  logAxiosError,
  useActiveMainCanvas,
  useCanvasId,
} from "@/utils/utils";
import { ExitFullScreenIcon, MinusIcon, PlusIcon } from "@radix-ui/react-icons";
import _ from "lodash";
import {
  ChevronDownIcon,
  ExpandIcon,
  LightbulbIcon,
  Loader2,
  TrashIcon,
} from "lucide-react";
import mermaid from "mermaid";
import React, { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import { TransformComponent, TransformWrapper } from "react-zoom-pan-pinch";
import { toast } from "sonner";
import { v4 as uuidv4 } from "uuid";
import { getContextFromFeaturePrd } from "../EngSpec/utils";
import { useEditPrd } from "./hooks/useEditPrd";
import { PRDVisualContext } from "./PRD";
import { useRunTerminalCommand } from "@/utils/devServerUtils";
import { TemplateStack } from "@/utils/templateProjects";
import axios from "axios";
import { STORYBOARD_TYPES } from "@/utils/constants";
import { updateCanvasStoryboards } from "@/store/supabaseSlice";
import { useDispatch } from "react-redux";
import { useCodebaseStore } from "@/store/zustand/codebaseStore";
import { getAxiosErorrMessage } from "@/utils/utils";
import { useSession } from "@clerk/clerk-react";
import { ActionButtonsRow } from "@/components/ui/ActionButtonsRow";

mermaid.initialize({
  startOnLoad: false,
  theme: "dark",
  securityLevel: "loose",
  suppressErrorRendering: true,
});

const throttledEditMermaidSupabase = _.throttle(
  async (supabaseClient: any, newMermaid: string, canvasId: string) => {
    await supabaseClient
      .from("feature_prd")
      .update({
        mermaid_diagram: newMermaid,
      })
      .eq("canvas_id", canvasId);
  },
  1000
);

const MermaidTab = React.memo(() => {
  const canvasId = useCanvasId();
  const selectedTemplateStack = useProjectCreateStore(
    (state) => state.selectedTemplateStack
  );

  const featurePrd = useFeaturePrdForCanvas();
  const featurePrdRef = useRef(featurePrd);
  useEffect(() => {
    featurePrdRef.current = featurePrd;
  }, [featurePrd]);

  const { session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();
  const generateMermaidParam = searchParams.get("generate-mermaid") === "t";

  const [generatingMermaid, setGeneratingMermaid] = useState(false);

  const visualContext =
    featurePrd?.canvas_visual_context as any as PRDVisualContext;

  const [hoveringContextIndex, setHoveringContextIndex] =
    // @ts-expect-error TS2345
    useState<number>(null);

  const onDbSnapshot = useSupabaseStore((state) => state.onDbSnapshot);

  const setAddContextMode = useLayoutStore((state) => state.setAddContextMode);
  const [mermaidCodeOpen, setMermaidCodeOpen] = useState(false);

  const setTriggerGenerateWireframes = useAiContextStore(
    (state) => state.setTriggerGenerateWireframes
  );

  const setTempAssistantMessage = useAiContextStore(
    (state) => state.setTempAssistantMessage
  );
  const setAddManualMessage = useAiContextStore(
    (state) => state.setAddManualMessage
  );
  const setSendChatMessage = useAiContextStore(
    (state) => state.setSendChatMessage
  );
  const sendChatMessage = useAiContextStore((state) => state.sendChatMessage);

  const projectLaunchStatus = useCodebaseStore(
    (state) => state.projectLaunchStatus
  );
  const projectLaunchStatusRef = useRef(projectLaunchStatus);
  useEffect(() => {
    projectLaunchStatusRef.current = projectLaunchStatus;
  }, [projectLaunchStatus]);

  const runTerminalCommand = useRunTerminalCommand();
  const runTerminalCommandRef = useRef(runTerminalCommand);
  useEffect(() => {
    runTerminalCommandRef.current = runTerminalCommand;
  }, [runTerminalCommand]);

  const refreshPlanInfo = usePlanInfoStore((state) => state.refreshPlanInfo);

  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const mermaidViewerRef = useRef(null);
  const fullscreenMermaidViewerRef = useRef(null);

  const { supabaseClient } = useSupabase();

  const dispatch = useDispatch();

  const [openFullScreen, setOpenFullScreen] = useState(false);

  const [zoomInput, setZoomInput] = useState("100");

  const [abortController, setAbortController] = useState(new AbortController());

  const stopAiTrigger = useAiActionStore((state) => state.stopAiTrigger);

  const mainCanvas = useActiveMainCanvas();

  const reactFramework = useProjectCreateStore(
    (state: any) => state.reactFramework
  );

  const isChatLoading = useIsChatLoading();

  const [clearLoadingStateWhenAiLoads, setClearLoadingStateWhenAiLoads] =
    useState(false);

  /**
   * Clear the storyboard loading state when the AI loads
   */
  useEffect(() => {
    const mainStoryboardLoading = mainCanvas?.storyboards?.find(
      (sb) => sb.type === STORYBOARD_TYPES.APPLICATION && sb.aiGenerating
    );

    if (!isChatLoading && mainStoryboardLoading) {
      setClearLoadingStateWhenAiLoads(false);

      const mainStoryboard = mainCanvas.storyboards.find(
        (sb) => sb.type === STORYBOARD_TYPES.APPLICATION
      );

      if (!mainStoryboard) {
        return;
      }

      const newMainStoryboard = {
        ...mainStoryboard,
        aiGenerating: false,
      };

      updateCanvasStoryboards(
        supabaseClient,
        dispatch,
        mainCanvas.storyboards.map((sb) =>
          sb.id === mainStoryboard.id ? newMainStoryboard : sb
        ),
        mainCanvas
      );
    }
  }, [isChatLoading, mainCanvas]);

  useEffect(() => {
    if (stopAiTrigger && abortController) {
      abortController.abort();
      // @ts-expect-error TS2345
      setAbortController(null);
    }
  }, [stopAiTrigger]);
  const onEditPrd = useEditPrd();

  const generateMermaid = async () => {
    setGeneratingMermaid(true);

    const messageText = "Generating a mermaid diagram to show the user flow";
    const progressMessage: ManualAssistantMessage = {
      id: uuidv4().toString(),
      text: messageText,
      progressItems: [
        {
          title: "New Mermaid Diagram",
          loading: true,
        },
      ],
    };
    setTempAssistantMessage(progressMessage);

    /**
     * Update based on the current response format returned from the vellum workflow
     */
    const updateMermaid = _.throttle(
      (response: { mermaidDiagram?: string }) => {
        const newMermaid = response?.mermaidDiagram || "";

        onDbSnapshot("featurePrds", {
          newItems: [{ ...featurePrdRef.current, mermaid_diagram: newMermaid }],
        });

        throttledEditMermaidSupabase(supabaseClient, newMermaid, canvasId);
      },
      100
    );

    try {
      const url = `${
        import.meta.env.VITE_BACKEND_BASE_URL
      }/canvases/${canvasId}/ai/vercel-ai/aiMermaidGenerator`;

      const { contextWithImages, userSelectionContext } =
        getContextFromFeaturePrd(featurePrd);

      const body = {
        feature_title: featurePrd?.title || "",
        feature_prd: featurePrd?.prd || "",
        visual_context: contextWithImages,
        user_selection_context: JSON.stringify(userSelectionContext),
      };

      const newAbortController = new AbortController();
      setAbortController(newAbortController);

      const supabaseJWT = await getUnexpiredToken(session);

      const stream = await generateJsonStream(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${supabaseJWT}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: newAbortController.signal,
      });

      let res = null;

      let partialResult = "";

      for await (let chunk of stream) {
        if (chunk.status === -1 && chunk.message === "intermediate") {
          const parsedChunk = JSON.parse(chunk.data);

          if (parsedChunk.type === "text-delta") {
            partialResult += parsedChunk.chunk;
          }

          const chunkJson = fixJson(partialResult).fixedObj;
          updateMermaid(chunkJson);
        } else {
          res = chunk;
          break;
        }
      }

      // @ts-expect-error TS18047
      if (res.status >= 400) {
        console.error({
          // @ts-expect-error TS18047
          msg: res.message,
          // @ts-expect-error TS18047
          internalErrorStr: res.internalErrorStr,
        });
        // @ts-expect-error TS18047
        throw new Error(res.message);
      }

      try {
        // @ts-expect-error TS18047
        const finalJson = JSON.parse(res.data.steps[0].text);

        updateMermaid(finalJson);
      } catch (e) {
        // Sometimes the last response is improperly formatted, but this is ok because the streaming
        // should have the full response
      }

      toast.success("Generated");
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          succeeded: true,
        })),
      });
    } catch (err) {
      logAxiosError("Error generating mermaid diagram", err);
      if (err.name !== "AbortError") {
        toast.error(`Error generating mermaid diagram: ` + err.message);
      }
      setAddManualMessage({
        ...progressMessage,
        progressItems: progressMessage.progressItems?.map((item) => ({
          ...item,
          failed: true,
        })),
      });
    } finally {
      setGeneratingMermaid(false);
      // @ts-expect-error TS2345
      setAbortController(null);
      refreshPlanInfo(session);
      // @ts-expect-error TS2345
      setTempAssistantMessage(null);
    }
  };

  const triggeredGenerateRef = useRef(false);

  useEffect(() => {
    if (generatingMermaid || triggeredGenerateRef.current || !session) return;

    if (generateMermaidParam && featurePrd?.prd && !generatingMermaid) {
      triggeredGenerateRef.current = true;
      generateMermaid().then(() => {
        setSearchParams(
          (prev) => {
            const newParams = {
              ...Object.fromEntries(prev.entries()),
            };
            delete newParams["generate-mermaid"];
            return newParams;
          },
          { preventScrollReset: true }
        );

        // Give the user a second to see what happened
        setTimeout(async () => {
          try {
            if (selectedTemplateStack === TemplateStack.BLANK) {
              setNewLayout(LeftTab.AIChat, LayoutMode.Design);
              setTriggerGenerateWireframes(true);
              return;
            }

            // Mark the main storyboard as generating
            const mainStoryboard = mainCanvas.storyboards.find(
              (sb) => sb.type === STORYBOARD_TYPES.APPLICATION
            );

            if (mainStoryboard) {
              const newMainStoryboard = {
                ...mainStoryboard,
                aiGenerating: true,
              };

              await updateCanvasStoryboards(
                supabaseClient,
                dispatch,
                mainCanvas.storyboards.map((sb) =>
                  sb.id === mainStoryboard.id ? newMainStoryboard : sb
                ),
                mainCanvas
              );

              setClearLoadingStateWhenAiLoads(true);
            }

            const newAbortController = new AbortController();
            setAbortController(newAbortController);

            const runSupabaseSetup = async () => {
              const setupProgressMessage: ManualAssistantMessage = {
                id: uuidv4().toString(),
                text: "Setting up Supabase project components",
                progressItems: [
                  {
                    title: "Running initial Supabase migration",
                    loading: true,
                  },
                ],
              };

              if (
                selectedTemplateStack === TemplateStack.SUPABASE_STRIPE ||
                selectedTemplateStack === TemplateStack.SUPABASE_POLAR
              ) {
                // @ts-expect-error TS18048
                setupProgressMessage.progressItems.push(
                  {
                    title: "Setting up get-plans edge function",
                    loading: true,
                  },
                  {
                    title: "Setting up payments-webhook edge function",
                    loading: true,
                  },
                  {
                    title: "Setting up create-checkout edge function",
                    loading: true,
                  }
                );
              }

              setTempAssistantMessage(setupProgressMessage);

              try {
                // Check if operation was aborted before starting
                if (newAbortController?.signal?.aborted) {
                  throw new Error("Operation aborted by user");
                }

                await runInitialSupabaseMigration(newAbortController);

                // Check if operation was aborted after migration
                if (newAbortController?.signal?.aborted) {
                  throw new Error("Operation aborted by user");
                }

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[0].loading = false;
                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[0].succeeded = true;

                if (
                  selectedTemplateStack === TemplateStack.SUPABASE_STRIPE ||
                  selectedTemplateStack === TemplateStack.SUPABASE_POLAR
                ) {
                  setTempAssistantMessage({ ...setupProgressMessage });
                  try {
                    // Check for abort before each edge function setup
                    if (newAbortController?.signal?.aborted) {
                      throw new Error("Operation aborted by user");
                    }

                    await runIntialSupabaseEdgeFunction(
                      newAbortController,
                      "get-plans/index.ts"
                    );
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[1].loading = false;
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[1].succeeded = true;
                    setTempAssistantMessage({ ...setupProgressMessage });

                    if (newAbortController?.signal?.aborted) {
                      throw new Error("Operation aborted by user");
                    }

                    await runIntialSupabaseEdgeFunction(
                      newAbortController,
                      "payments-webhook/index.ts"
                    );
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[2].loading = false;
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[2].succeeded = true;
                    setTempAssistantMessage({ ...setupProgressMessage });

                    if (newAbortController?.signal?.aborted) {
                      throw new Error("Operation aborted by user");
                    }

                    await runIntialSupabaseEdgeFunction(
                      newAbortController,
                      "create-checkout/index.ts"
                    );
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[3].loading = false;
                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems[3].succeeded = true;
                    setTempAssistantMessage({ ...setupProgressMessage });
                    setAddManualMessage({
                      ...setupProgressMessage,
                      afterText: "Supabase integration set up successfully.",
                    });
                  } catch (error) {
                    console.error("Error setting up edge functions:", error);

                    // @ts-expect-error TS18048
                    setupProgressMessage.progressItems.forEach((item) => {
                      if (item.loading) {
                        item.loading = false;
                        item.failed = true;
                      }
                    });

                    setTempAssistantMessage({ ...setupProgressMessage });
                    setAddManualMessage({
                      ...setupProgressMessage,
                      afterText: error.message,
                    });

                    if (error.message !== "Operation aborted by user") {
                      toast.error(
                        "Error setting up Supabase edge functions. You may need to set them up manually."
                      );
                    }
                  }
                } else {
                  setAddManualMessage({
                    ...setupProgressMessage,
                    afterText: "Supabase integration set up successfully.",
                  });
                }

                // @ts-expect-error TS2345
                setTempAssistantMessage(null);
              } catch (error) {
                console.error("Error in Supabase setup:", error);

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems.forEach((item) => {
                  if (item.loading) {
                    item.loading = false;
                    item.failed = true;
                  }
                });

                setAddManualMessage({
                  ...setupProgressMessage,
                  afterText: error.message,
                });
                // @ts-expect-error TS2345
                setTempAssistantMessage(null);

                if (error.message !== "Operation aborted by user") {
                  toast.error(error.message);
                }
              }
            };

            const runConvexSetup = async () => {
              const setupProgressMessage: ManualAssistantMessage = {
                id: uuidv4().toString(),
                text: "Logging in to Convex",
                progressItems: [
                  {
                    title: "Waiting for project to load",
                    loading: true,
                  },
                  {
                    title: "Running convex connect",
                  },
                ],
              };

              setTempAssistantMessage(setupProgressMessage);

              try {
                // Check if operation was aborted before starting
                if (newAbortController?.signal?.aborted) {
                  throw new Error("Operation aborted");
                }

                // Add interval to wait for project to load with 5 minute timeout
                await new Promise((resolve, reject) => {
                  const startTime = Date.now();
                  const maxWaitTime = 5 * 60 * 1000; // 5 minutes in milliseconds

                  const checkInterval = setInterval(() => {
                    // Check if operation was aborted
                    if (newAbortController?.signal?.aborted) {
                      clearInterval(checkInterval);
                      reject(new Error("Operation aborted by user"));
                      return;
                    }

                    // Check if project is loaded
                    if (projectLaunchStatusRef.current === "succeeded") {
                      clearInterval(checkInterval);
                      resolve(true);
                      return;
                    }

                    // Check if we've exceeded the maximum wait time
                    if (Date.now() - startTime > maxWaitTime) {
                      clearInterval(checkInterval);
                      reject(
                        new Error("Timed out waiting for project to load")
                      );
                      return;
                    }
                  }, 1000); // Check every second
                });

                // Check if operation was aborted after waiting
                if (newAbortController?.signal?.aborted) {
                  throw new Error("Operation aborted by user");
                }

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[0].loading = false;
                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[0].succeeded = true;

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[1].loading = true;

                setTempAssistantMessage({
                  ...setupProgressMessage,
                });

                const convexSyncCommand = `npx convex login --device-name ${uuidv4()} --no-open`;

                const { success, error } = await runTerminalCommandRef.current({
                  command: convexSyncCommand,
                  abortController: newAbortController,
                });

                if (!success) {
                  if (newAbortController?.signal.aborted) {
                    throw new Error("Operation aborted by user");
                  }

                  throw new Error(error || "Unknown error");
                }

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[1].loading = false;
                // @ts-expect-error TS18048
                setupProgressMessage.progressItems[1].succeeded = true;

                setAddManualMessage({
                  ...setupProgressMessage,
                  afterText: "Convex integration set up successfully.",
                });

                // @ts-expect-error TS2345
                setTempAssistantMessage(null);
              } catch (error) {
                console.error("Error in Convex setup:", error);

                // @ts-expect-error TS18048
                setupProgressMessage.progressItems.forEach((item) => {
                  if (!item.succeeded) {
                    item.loading = false;
                    item.failed = true;
                  }
                });

                setAddManualMessage({
                  ...setupProgressMessage,
                  afterText: "Error setting up Convex:\n" + error.message,
                });
                // @ts-expect-error TS2345
                setTempAssistantMessage(null);

                if (error.message !== "Operation aborted by user") {
                  toast.error(
                    "Error setting up Convex. You need to set it up manually in the Convex tab."
                  );
                }
              }
            };

            if (selectedTemplateStack.includes("SUPABASE")) {
              await runSupabaseSetup();
            } else if (selectedTemplateStack.includes("CONVEX")) {
              await runConvexSetup();
            }

            if (newAbortController?.signal.aborted) {
              // Don't continue if the user aborted
              return;
            }

            let message =
              "Template Generate Message:\n\nFirst, read and understand the existing codebase. Start with working on the landing page design and copy. Modify this template to implement what's in the PRD. Start with working on the landing page.";

            if (
              selectedTemplateStack?.includes("CLERK") ||
              selectedTemplateStack?.includes("SUPABASE")
            ) {
              message += `\n\nPlease note that basic authentication is already implemented in this codebase using ${
                selectedTemplateStack?.includes("CLERK") ? "Clerk" : "Supabase"
              }`;

              if (
                selectedTemplateStack?.includes("STRIPE") ||
                selectedTemplateStack?.includes("POLAR")
              ) {
                message += ` and basic payments functionality has been implemented using ${
                  selectedTemplateStack?.includes("STRIPE") ? "Stripe" : "Polar"
                }`;
              }

              message +=
                ". Please do not overwrite or change this implementation unless explicitly instructed by the user.";
            }

            message +=
              "\n\nAfter you have finished at the very end add storyboards to the canvas to show the components in the app (home route is already shown).";

            setSendChatMessage({
              sendChatMessage: message,
            });
          } catch (error) {
            console.error("Error in setTimeout callback:", error);
            toast.error(
              "An error occurred while setting up the project. Please try again."
            );
          } finally {
            // @ts-expect-error TS2345
            setAbortController(null);
          }
        }, 1000);
      });
    }
  }, [generateMermaidParam, featurePrd?.prd, Boolean(session)]);

  const runInitialSupabaseMigration = async (controller: AbortController) => {
    try {
      const supabaseJWT = await getUnexpiredToken(session);
      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/db/supabase/migration`,
        {
          relativeMigrationFilePath: "supabase/migrations/initial-setup.sql",
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseJWT}`,
          },
          signal: controller?.signal,
        }
      );

      return true;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new Error("Supabase migration aborted by user");
      }
      console.error("Error running Supabase migration:", error);
      throw new Error(
        `Failed to run Supabase migration: ${getAxiosErorrMessage(error)}`
      );
    }
  };

  const runIntialSupabaseEdgeFunction = async (
    controller: AbortController,
    fileName: string
  ) => {
    const supabaseJWT = await getUnexpiredToken(session);
    try {
      const response = await axios.post(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/db/supabase/edgeFunctionDeployment`,
        {
          relativeFunctionFilePath: `supabase/functions/${fileName}`,
        },
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${supabaseJWT}`,
          },
          signal: controller?.signal,
        }
      );

      return true;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new Error("Supabase edge function deployment aborted by user");
      }
      console.error(
        `Error deploying Supabase edge function ${fileName}:`,
        getAxiosErorrMessage(error)
      );
      throw new Error(
        `Failed to deploy edge function: ${getAxiosErorrMessage(error)}`
      );
    }
  };

  const renderMermaid = React.useCallback(async () => {
    try {
      const { svg } = await mermaid.render(
        "mermaidSvgId",
        // @ts-expect-error TS2345
        featurePrd?.mermaid_diagram
      );

      if (openFullScreen) {
        // @ts-expect-error TS18047
        fullscreenMermaidViewerRef.current.innerHTML = svg;
      } else {
        // @ts-expect-error TS18047
        mermaidViewerRef.current.innerHTML = svg;
      }
    } catch (e) {}
  }, [openFullScreen, featurePrd?.mermaid_diagram]);

  useEffect(() => {
    if (!featurePrd?.mermaid_diagram || !mermaidViewerRef.current) {
      return;
    }

    renderMermaid();
  }, [
    openFullScreen,
    featurePrd?.mermaid_diagram,
    fullscreenMermaidViewerRef.current,
    mermaidViewerRef.current,
  ]);

  return (
    <div className="flex flex-col gap-2 pb-4 px-0">
      <ActionButtonsRow />
      <div className="flex items-center justify-between">
        <div className="text-sm">User Flow</div>
        <Button
          variant="primary"
          size="xs"
          onClick={() => {
            track("AI_PROMPT_REQUESTED", { type: "MERMAID" });
            generateMermaid();
          }}
          disabled={generatingMermaid}
          className="h-7"
        >
          <div
            className={cn("animate-spin mr-1", !generatingMermaid && "hidden")}
          >
            <Loader2 className="h-4 w-4" />
          </div>
          Generate
        </Button>
      </div>

      <div className="flex rounded-[5px] bg-secondary-200 justify-center w-full relative">
        <Dialog
          open={openFullScreen}
          onOpenChange={(open) => setOpenFullScreen(open)}
        >
          <DialogTrigger asChild className="absolute z-[1] top-2 right-2">
            <ExpandIcon className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900" />
          </DialogTrigger>
          <DialogContent
            className="w-[80vw] h-[80vh] max-w-[80vw] flex flex-col"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <DialogHeader>
              <DialogTitle className="mb-3">Mermaid Diagram</DialogTitle>
            </DialogHeader>
            <div className="flex grow overflow-hidden bg-secondary-200 relative cursor-grab active:cursor-grabbing pointer-events-auto">
              <TransformWrapper
                initialScale={1}
                minScale={1}
                maxScale={8}
                wheel={{
                  step: 0.05,
                }}
                pinch={{
                  step: 0.05,
                }}
                onTransformed={(e) => {
                  // Update zoom input when zooming with buttons/gestures
                  const zoomPercent = Math.round(e.state.scale * 100);
                  setZoomInput(zoomPercent.toString());
                }}
              >
                {({ zoomIn, zoomOut, setTransform, instance }) => {
                  const currentScale =
                    Math.round(instance.transformState.scale * 100) / 100;

                  const setScale = (newScale: number) => {
                    const { positionX, positionY } = instance.transformState;
                    // @ts-expect-error TS2339
                    const { offsetWidth, offsetHeight } =
                      instance.wrapperComponent;
                    const centerX = offsetWidth / 2;
                    const centerY = offsetHeight / 2;
                    const newPosX =
                      centerX -
                      (centerX - positionX) * (newScale / currentScale);
                    const newPosY =
                      centerY -
                      (centerY - positionY) * (newScale / currentScale);

                    setTransform(newPosX, newPosY, newScale);
                  };

                  const executeZoomInput = () => {
                    const numValue = parseInt(zoomInput);
                    if (isNaN(numValue)) {
                      setZoomInput("100");
                      setScale(1);
                    } else {
                      setScale(numValue / 100);
                    }
                  };

                  return (
                    <>
                      <TransformComponent
                        wrapperStyle={{ width: "100%", height: "100%" }}
                        contentStyle={{
                          width: "100%",
                          height: "min-content",
                        }}
                      >
                        <div
                          ref={fullscreenMermaidViewerRef}
                          className="w-full items-center justify-center overflow-y-scroll hide-scrollbar rounded-[5px] h-min p-4"
                        />
                      </TransformComponent>
                      <div className="absolute bottom-4 right-4 text-white flex flex-row text-xs gap-2">
                        <div className="shadow-md bg-background-tertiary rounded-sm">
                          <Button
                            className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent w-7 h-7"
                            onClick={() => setScale(1)}
                            disabled={currentScale <= 1}
                          >
                            <ExitFullScreenIcon />
                          </Button>
                        </div>
                        <div className="flex shadow-md bg-background-tertiary rounded-sm divide-x divide-secondary-200 overflow-hidden">
                          <Button
                            className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent w-7 h-7 rounded-none"
                            onClick={() => zoomOut()}
                            disabled={currentScale <= 1}
                          >
                            <MinusIcon />
                          </Button>
                          <div className="w-12 flex justify-center items-center cursor-pointer h-7">
                            <input
                              className="w-full h-full bg-transparent focus:outline-none py-1 text-center text-2xs focus-visible::ring-1 focus-visible:ring-ring focus-visible:outline-none focus:border focus:border-primary-0 hover:bg-background-accent pr-2"
                              maxLength={3}
                              value={zoomInput}
                              onChange={(e) => {
                                const value = e.target.value;
                                setZoomInput(value);
                              }}
                              onBlur={() => executeZoomInput()}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                  executeZoomInput();
                                }
                              }}
                            />
                            <div className="w-3.5 -ml-3.5 text-left pointer-events-none">
                              %
                            </div>
                          </div>
                          <Button
                            className="py-1 px-1 flex justify-center items-center cursor-pointer hover:bg-background-accent h-7 w-7 rounded-none"
                            onClick={() => zoomIn()}
                            disabled={currentScale >= 8}
                          >
                            <PlusIcon />
                          </Button>
                        </div>
                      </div>
                    </>
                  );
                }}
              </TransformWrapper>
            </div>
          </DialogContent>
        </Dialog>

        <div className="margin-auto w-full">
          <TransformWrapper>
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "350px" }}
              contentStyle={{
                width: "100%",
                height: "min-content",
              }}
            >
              <div
                ref={mermaidViewerRef}
                className="flex items-center justify-center grow px-12 p-4"
              />
            </TransformComponent>
          </TransformWrapper>
          <div
            className={cn(
              "flex flex-col m-2 rounded-xl overflow-hidden",
              mermaidCodeOpen && "border"
            )}
          >
            <div
              className={cn(
                "select-none flex text-text-secondary hover:text-text-primary bg-background-tertiary p-2 mb-0 px-3 text-xs rounded-lg justify-between items-center cursor-pointer",
                mermaidCodeOpen && "rounded-b-none"
              )}
              onClick={() => {
                setMermaidCodeOpen(!mermaidCodeOpen);
              }}
            >
              <div>Mermaid Code</div>
              <ChevronDownIcon
                className={cn(
                  "w-3 h-3 ml-2",
                  mermaidCodeOpen && "transform rotate-180"
                )}
              />
            </div>

            <div className={cn(!mermaidCodeOpen && "hidden")}>
              <SimpleMonacoEditor
                className={"h-[220px] pl-2 pt-1 pb-1 bg-background-secondary"}
                value={featurePrd?.mermaid_diagram || ""}
                fileKey={"mermaid_diagram"}
                language="mermaid"
                theme="mermaid-dark"
                onChange={(newValue: string) => {
                  onEditPrd("mermaid_diagram", newValue, true);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <Separator className="mt-1" />

      <div className="flex items-center justify-between">
        <div className="text-sm py-1">Context</div>
        {Boolean(visualContext?.length) && (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAddContextMode(true)}
            className="h-7"
          >
            Add Visual Context
          </Button>
        )}
      </div>

      <div
        className={cn(
          "h-[220px] pl-[15px] pt-2.5 bg-secondary-200 rounded-[5px] justify-center items-center gap-[25px] inline-flex container overflow-y-scroll hide-scrollbar",
          visualContext?.length && "items-start justify-start pt-3 px-2 w-full"
        )}
      >
        {visualContext?.length ? (
          <div className="w-full flex flex-col gap-1">
            {visualContext.map((context, index) => (
              <div
                key={index}
                className="flex flex-row items-center justify-between w-full pr-2"
                onMouseEnter={() => setHoveringContextIndex(index)}
                // @ts-expect-error TS2345
                onMouseLeave={() => setHoveringContextIndex(undefined)}
              >
                <div className="flex flex-row items-center justify-start gap-x-1.5">
                  <div className="w-[22px] h-[22px] rounded-[3px] justify-center items-center flex">
                    <div className="w-3.5 h-3.5 relative">
                      <LightbulbIcon className="w-3.5 h-3.5 text-white" />
                    </div>
                  </div>
                  <div className="text-white text-xs font-medium font-['Inter']">
                    {context.text + (context.url ? ` [${context.url}]` : "")}
                  </div>
                </div>
                {hoveringContextIndex === index && (
                  <TrashIcon
                    className="w-4 h-4 cursor-pointer text-secondary-800 hover:text-secondary-900"
                    onClick={() => {
                      const newContext = visualContext.filter(
                        (ctx, ctxIndex) => ctxIndex !== index
                      );

                      onEditPrd("canvas_visual_context", newContext);
                    }}
                  />
                )}
              </div>
            ))}
          </div>
        ) : (
          <Button
            variant="primary"
            size="sm"
            onClick={() => setAddContextMode(true)}
            className="h-7"
          >
            Add Visual Context
          </Button>
        )}
      </div>
    </div>
  );
});

export default MermaidTab;
